
import React, { useState, useEffect } from "react";
import { database, auth } from '../firebase';
import { ref, onValue, off } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import ReactDOMServer from 'react-dom/server';

// Helper: Extract city from address
const getCityFromAddress = (address) => {
  if (!address) return 'Unknown City';
  const addressParts = address.trim().split(/\s+/);
  return addressParts.length > 0 ? addressParts[addressParts.length - 1] : 'Unknown City';
};

// Filter Section Component
const FilterSection = ({ title, options, selected, onChange }) => {
  const handleCheckboxChange = (option) => {
    if (option === options[0]) { // "All" option
      if (selected.includes(option)) {
        onChange([]);
      } else {
        onChange([...options]);
      }
    } else {
      const newSelected = selected.includes(option)
        ? selected.filter(item => item !== option && item !== options[0])
        : [...selected.filter(item => item !== options[0]), option];
      
      if (newSelected.length === options.length - 1) {
        onChange([options[0], ...newSelected]);
      } else {
        onChange(newSelected);
      }
    }
  };

  return (
    <div style={{
      backgroundColor: '#f8fafc',
      padding: '16px',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      transition: 'all 0.3s ease',
      maxWidth: '220px',
      marginBottom: '16px',
    }}>
      <h2 style={{
        fontSize: '16px',
        fontWeight: '600',
        color: '#1e293b',
        marginBottom: '8px',
        borderBottom: '2px solid #3b82f6',
        paddingBottom: '6px',
      }}>{title}</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {options.map((option) => (
          <label key={option} style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            cursor: 'pointer',
            padding: '6px',
            borderRadius: '6px',
            transition: 'background-color 0.2s',
            backgroundColor: selected.includes(option) ? '#eff6ff' : 'transparent',
          }}
          onMouseEnter={(e) => {
            if (!selected.includes(option)) {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
            }
          }}
          onMouseLeave={(e) => {
            if (!selected.includes(option)) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
          >
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={() => handleCheckboxChange(option)}
              style={{
                height: '14px',
                width: '14px',
                accentColor: '#3b82f6',
                cursor: 'pointer',
              }}
            />
            <span style={{ 
              fontSize: '14px',
              color: '#475569',
              userSelect: 'none',
              fontWeight: selected.includes(option) ? '500' : '400',
            }}>{option}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

// Purchase Report Display Component
const PurchaseReportDisplay = ({ formData, filters, branchProfile, purchaseData }) => {
  // Helper function to parse date
  const parseInvoiceDate = (dateString) => {
    if (!dateString) return null;
    // Handle DD-MM-YYYY or DD/MM/YYYY
    if (/^\d{2}[-\/]\d{2}[-\/]\d{4}$/.test(dateString)) {
      const parts = dateString.split(/[-\/]/);
      return new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
    }
    // Handle YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const parts = dateString.split('-');
      return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    }
    // Fallback to default Date parsing
    return new Date(dateString);
  };

  // Format date to DD/MM/YYYY for display
  const formatDisplayDate = (dateString) => {
    const date = parseInvoiceDate(dateString);
    return !date || isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };
  let filteredPurchaseData = purchaseData;

    // Modified date filter for all report types, including invoicewise
    if (formData.fromDate && formData.toDate) {
      const fromDate = new Date(formData.fromDate);
      fromDate.setHours(0, 0, 0, 0); // Start of the selected from date
      const toDate = new Date(formData.toDate);
      toDate.setHours(23, 59, 59, 999); // End of the selected to date
      filteredPurchaseData = filteredPurchaseData.filter(item => {
        const itemDate = parseInvoiceDate(item.invoiceDate);
        return itemDate && !isNaN(itemDate.getTime()) && itemDate >= fromDate && itemDate <= toDate;
      });
    }
 

  // Modified date filter for datewise reports
  if (formData.searchBy === "datewise") {
    const fromDate = new Date(formData.fromDate);
    fromDate.setHours(0, 0, 0, 0); // Start of the selected from date
    const toDate = new Date(formData.toDate);
    toDate.setHours(23, 59, 59, 999); // End of the selected to date
    filteredPurchaseData = filteredPurchaseData.filter(item => {
      const itemDate = parseInvoiceDate(item.invoiceDate);
      return itemDate && !isNaN(itemDate.getTime()) && itemDate >= fromDate && itemDate <= toDate;
    });
  }
// Apply supplier filter (for all report types, not just supplierwise detail)
if (!filters.supplier.includes("All Supplier Names")) {
  filteredPurchaseData = filteredPurchaseData.filter(item => 
    filters.supplier.includes(item.supplier)
  );
}

// Apply group filter (for all report types, not just groupwise)
if (!filters.group.includes("All Group Names")) {
  filteredPurchaseData = filteredPurchaseData.filter(item => 
    filters.group.includes(item.groupName)
  );
}

// Apply product filter (for all report types)
if (!filters.product.includes("All Product Names")) {
  filteredPurchaseData = filteredPurchaseData.filter(item => 
    filters.product.includes(item.productName)
  );
}
  // Initialize displayData
  let displayData = [];

  // Process data based on search criteria and report type
  if ((formData.searchBy === "datewise" || formData.searchBy === "supplierwise" || formData.searchBy === "groupwise") && formData.reportType === "abstract") {
    const groupedData = filteredPurchaseData.reduce((acc, item) => {
      const key = formData.searchBy === "datewise" ? item.invoiceDate : 
                  formData.searchBy === "supplierwise" ? item.supplier : 
                  item.groupName;
      if (!acc[key]) {
        acc[key] = {
          key: key,
          mrnNos: [],
          value: 0,
          discAmt: 0,
          cgstAmt: 0,
          sgstAmt: 0,
          gstAmt: 0,
          netValue: 0,
          count: 0
        };
      }
      acc[key].mrnNos.push(item.mrnNo);
      acc[key].value += item.grossAmt;
      acc[key].discAmt += item.discAmt;
      acc[key].cgstAmt += item.cgstAmt;
      acc[key].sgstAmt += item.sgstAmt;
      acc[key].gstAmt += item.cgstAmt + item.sgstAmt + item.igstAmt;
      acc[key].netValue += item.netValue;
      acc[key].count += 1;
      return acc;
    }, {});
    displayData = Object.values(groupedData);
    displayData.sort((a, b) => {
      if (formData.searchBy === "datewise") return parseInvoiceDate(a.key) - parseInvoiceDate(b.key);
      return a.key.localeCompare(b.key);
    });
  } else if (formData.searchBy === "productwise" && formData.reportType === "abstract") {
    const groupedData = filteredPurchaseData.reduce((acc, item) => {
      const key = item.productName;
      if (!acc[key]) {
        acc[key] = {
          productName: item.productName,
          commodityNo: item.commodityNo || '',
          barcode: item.barcode || '',
          batch: item.batch || '',
          qty: 0,
          grossAmt: 0,
          discAmt: 0,
          cgstAmt: 0,
          sgstAmt: 0,
          gstAmt: 0,
          netValue: 0
        };
      }
      acc[key].qty += item.qty;
      acc[key].grossAmt += item.grossAmt;
      acc[key].discAmt += item.discAmt;
      acc[key].cgstAmt += item.cgstAmt;
      acc[key].sgstAmt += item.sgstAmt;
      acc[key].gstAmt += item.cgstAmt + item.sgstAmt + item.igstAmt;
      acc[key].netValue += item.netValue;
      return acc;
    }, {});
    displayData = Object.values(groupedData);
    displayData.sort((a, b) => a.productName.localeCompare(b.productName));
  } else {
    // For detail reports and other cases
    displayData = [...filteredPurchaseData];
    displayData.sort((a, b) => {
      if (formData.searchBy === "supplierwise") {
        return a.supplier.localeCompare(b.supplier) || parseInvoiceDate(a.invoiceDate) - parseInvoiceDate(b.invoiceDate);
      }
      if (formData.searchBy === "groupwise") {
        return a.groupName.localeCompare(b.groupName) || parseInvoiceDate(a.invoiceDate) - parseInvoiceDate(b.invoiceDate);
      }
      if (formData.searchBy === "productwise") {
        return a.productName.localeCompare(b.productName) || parseInvoiceDate(a.invoiceDate) - parseInvoiceDate(b.invoiceDate);
      }
      if (formData.searchBy === "invoicewise") {
        return a.invoiceNo.localeCompare(b.invoiceNo) || parseInvoiceDate(a.invoiceDate) - parseInvoiceDate(b.invoiceDate);
      }
      return parseInvoiceDate(a.invoiceDate) - parseInvoiceDate(b.invoiceDate);
    });
  }

  // Ensure displayData is always an array
  displayData = Array.isArray(displayData) ? displayData : [];

  const city = branchProfile.city || getCityFromAddress(branchProfile.address);

  // Calculate totals
  const totalQty = displayData.reduce((sum, item) => sum + (parseFloat(item.qty) || 0), 0);
  const totalValue = displayData.reduce((sum, item) => sum + (parseFloat(item.value) || parseFloat(item.grossAmt) || 0), 0);
  const totalDiscAmt = displayData.reduce((sum, item) => sum + (parseFloat(item.discAmt) || 0), 0);
  const totalCgstAmt = displayData.reduce((sum, item) => sum + (parseFloat(item.cgstAmt) || 0), 0);
  const totalSgstAmt = displayData.reduce((sum, item) => sum + (parseFloat(item.sgstAmt) || 0), 0);
  const totalIgstAmt = displayData.reduce((sum, item) => sum + (parseFloat(item.igstAmt) || 0), 0);
  const totalGstAmt = totalCgstAmt + totalSgstAmt + totalIgstAmt;
  const totalNetValue = displayData.reduce((sum, item) => sum + (parseFloat(item.netValue) || 0), 0);

  const renderTableHeaders = () => {
    if ((formData.searchBy === "datewise" || formData.searchBy === "supplierwise" || formData.searchBy === "groupwise") && formData.reportType === "abstract") {
      return (
        <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            {formData.searchBy === "datewise" ? "Date" : formData.searchBy === "supplierwise" ? "Supplier" : "Group Name"}
          </th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Value</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Disc.</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>CGST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>SGST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>GST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Net Value</th>
        </tr>
      );
    } else if (formData.searchBy === "productwise" && formData.reportType === "abstract") {
      return (
        <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>ProductName</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Commodity No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Barcode</th>
          {/* <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Batch No</th> */}
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Quantity</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Value</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Disc.</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>CGST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>SGST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>GST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Net Value</th>
        </tr>
      );
    } else if (formData.searchBy === "invoicewise" && formData.reportType === "detail") {
      return (
        <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Invoice No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Invoice Date</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Supplier</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Product Name</th>
          {/* <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Batch</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Expiry</th> */}
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Qty</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>PRate</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Gross Amt.</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Disc.Amt.</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>CGST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>SGST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>GST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Net Value</th>
        </tr>
      );
    } else if (formData.searchBy === "supplierwise" && formData.reportType === "detail") {
      return (
        <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>MRN No.</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>MRN Dt.</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Gross Amt.</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Disc.Amt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>CGST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>SGST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>GST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Others</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Net Value</th>
        </tr>
      );
    } else {
      return (
        <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Product Name</th>
          {/* <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Batch</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Expiry</th> */}
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Qty</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Average PRate</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Gross Amt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Disc.Amt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>CGST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>SGST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>GST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Net Value</th>
        </tr>
      );
    }
  };

  const renderTableRows = () => {
    if (displayData.length === 0) {
      const colSpan = (formData.searchBy === "datewise" || formData.searchBy === "supplierwise" || formData.searchBy === "groupwise") && formData.reportType === "abstract" ? 8 : 
                     formData.searchBy === "productwise" && formData.reportType === "abstract" ? 12 :
                     formData.searchBy === "invoicewise" && formData.reportType === "detail" ? 15 :
                     (formData.searchBy === "supplierwise" && formData.reportType === "detail") ? 10 : 12;

      return (
        <tr>
          <td colSpan={colSpan} style={{ 
            border: '1px solid #cbd5e1', 
            padding: '30px', 
            textAlign: 'center', 
            color: '#64748b', 
            fontStyle: 'italic',
            fontSize: '14px',
            backgroundColor: '#f8fafc'
          }}>
            No purchase data found for the selected criteria
          </td>
        </tr>
      );
    }

    return displayData.map((item, index) => {
      if ((formData.searchBy === "datewise" || formData.searchBy === "supplierwise" || formData.searchBy === "groupwise") && formData.reportType === "abstract") {
        return (
          <tr key={item.key} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>
              {formData.searchBy === "datewise" ? formatDisplayDate(item.key) : item.key}
            </td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.value) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.discAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.cgstAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.sgstAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.gstAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.netValue) || 0).toFixed(2)}</td>
          </tr>
        );
      } else if (formData.searchBy === "productwise" && formData.reportType === "abstract") {
        return (
          <tr key={item.productName} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.productName}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.commodityNo || 'N/A'}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.barcode || 'N/A'}</td>
            {/* <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.batch || 'N/A'}</td> */}
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.qty) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.grossAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.discAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.cgstAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.sgstAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.gstAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.netValue) || 0).toFixed(2)}</td>
          </tr>
        );
      } else if (formData.searchBy === "invoicewise" && formData.reportType === "detail") {
        return (
          <tr key={`${item.invoiceId}-${item.productName}-${index}`} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.invoiceNo || 'N/A'}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{formatDisplayDate(item.invoiceDate)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.supplier}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.productName}</td>
            {/* <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.batch || 'N/A'}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.eDate || 'N/A'}</td> */}
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.qty) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.pRate) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.grossAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.discAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.cgstAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.sgstAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{((parseFloat(item.cgstAmt) || 0) + (parseFloat(item.sgstAmt) || 0)).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.netValue) || 0).toFixed(2)}</td>
          </tr>
        );
      } else if (formData.searchBy === "supplierwise" && formData.reportType === "detail") {
        return (
          <tr key={`${item.invoiceId}-${item.productName}-${index}`} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.mrnNo || 'N/A'}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{formatDisplayDate(item.invoiceDate)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.grossAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.discAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.cgstAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.sgstAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{((parseFloat(item.cgstAmt) || 0) + (parseFloat(item.sgstAmt) || 0)).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.cessAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.netValue) || 0).toFixed(2)}</td>
          </tr>
        );
      } else {
        return (
          <tr key={`${item.invoiceId}-${item.productName}-${index}`} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.productName}</td>
            {/* <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.batch || 'N/A'}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.eDate || 'N/A'}</td> */}
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.qty) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.pRate) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.grossAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.discAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.cgstAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.sgstAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{((parseFloat(item.cgstAmt) || 0) + (parseFloat(item.sgstAmt) || 0)).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.netValue) || 0).toFixed(2)}</td>
          </tr>
        );
      }
    });
  };

 const renderTotalRow = () => {
  if (displayData.length === 0) return null;

  if ((formData.searchBy === "datewise" || formData.searchBy === "supplierwise" || formData.searchBy === "groupwise") && formData.reportType === "abstract") {
    return (
      <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
        <td colSpan="2" style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>
          Grand Total
        </td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalValue.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalDiscAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalCgstAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalSgstAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalGstAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalNetValue.toFixed(2)}</td>
      </tr>
    );
  } else if (formData.searchBy === "productwise" && formData.reportType === "abstract") {
    return (
      <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
        <td colSpan="4" style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>
          Grand Total
        </td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalQty.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalValue.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalDiscAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalCgstAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalSgstAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalGstAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalNetValue.toFixed(2)}</td>
      </tr>
    );
  } else if (formData.searchBy === "invoicewise" && formData.reportType === "detail") {
    return (
      <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
        <td colSpan="5" style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>
          Grand Total
        </td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalQty.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>-</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalValue.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalDiscAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalCgstAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalSgstAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalGstAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalNetValue.toFixed(2)}</td>
      </tr>
    );
  } else if (formData.searchBy === "supplierwise" && formData.reportType === "detail") {
    return (
      <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
        <td colSpan="3" style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>
          Grand Total
        </td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalValue.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalDiscAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalCgstAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalSgstAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalGstAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>0.00</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalNetValue.toFixed(2)}</td>
      </tr>
    );
  } else {
    // This is the default case that handles datewise + detail and other combinations
    // For datewise + detail, the table has 10 columns (S.No + Product Name + Qty + Average PRate + Gross Amt + Disc.Amt + CGSTAmnt + SGST Amnt + GST Amnt + Net Value)
    const colSpan = formData.searchBy === "datewise" && formData.reportType === "detail" ? 2 : 2;
    
    return (
      <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
        <td colSpan={colSpan} style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>
          Grand Total
        </td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalQty.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>-</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalValue.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalDiscAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalCgstAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalSgstAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalGstAmt.toFixed(2)}</td>
        <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalNetValue.toFixed(2)}</td>
      </tr>
    );
  }
};

  return (
    <div style={{
      backgroundColor: 'white',
      padding: '30px',
      marginTop: '20px',
      border: '2px solid #3b82f6',
      borderRadius: '12px',
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      pageBreakInside: 'avoid',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
    }} className="print-only">
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ 
            fontSize: '24px', 
            fontWeight: 'bold', 
            margin: '0', 
            marginBottom: '15px',
            color: '#1e293b',
            textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
          }}>
            {branchProfile.branchName}
          </h1>
          <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#475569' }}>
            <div style={{ marginBottom: '8px', fontSize: '16px', fontWeight: '600', color: '#3b82f6' }}>
              {formData.searchBy.charAt(0).toUpperCase() + formData.searchBy.slice(1)} Purchase Report
            </div>
            <div style={{ display: 'flex', gap: '20px', marginTop: '10px' }}>
              <span><strong>From:</strong> {new Date(formData.fromDate).toLocaleDateString('en-GB')}</span>
              <span><strong>To:</strong> {new Date(formData.toDate).toLocaleDateString('en-GB')}</span>
            </div>
          </div>
        </div>
        <div style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
          color: 'white',
          padding: '15px 25px',
          borderRadius: '15px',
          textAlign: 'center',
          minWidth: '150px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{city}</div>
          <div style={{ fontSize: '12px', marginTop: '5px', opacity: '0.9' }}>
            Option: {formData.reportType === 'detail' ? 'Detail' : 'Abstract'}
          </div>
        </div>
      </div>

      {/* Purchase Table */}
      <div style={{ overflowX: 'auto' }}>
     
<table style={{ 
  width: '100%', 
  borderCollapse: 'collapse', 
  fontSize: '11px', 
  border: '2px solid #3b82f6',
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
}} className={
  formData.searchBy === "invoicewise" && formData.reportType === "detail" ? "invoicewise-detail" :
  formData.searchBy === "productwise" && formData.reportType === "abstract" ? "productwise-abstract" :
  formData.searchBy === "supplierwise" && formData.reportType === "detail" ? "supplierwise-detail" :
  (formData.searchBy === "datewise" || formData.searchBy === "supplierwise" || formData.searchBy === "groupwise") && formData.reportType === "abstract" ? "abstract" :
  "default-detail"
}>
          <thead>
            {renderTableHeaders()}
          </thead>
          <tbody>
            {renderTableRows()}
            {renderTotalRow()}
          </tbody>
        </table>
      </div>

      {/* Summary Section */}
      {displayData.length > 0 && (
        <div style={{ 
          marginTop: '30px', 
          padding: '20px', 
          backgroundColor: '#f8fafc', 
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            marginBottom: '15px',
            color: '#1e293b'
          }}>Summary</h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
            gap: '15px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Records</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#3b82f6' }}>{displayData.length}</div>
            </div>
            {/* <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Quantity</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#059669' }}>{totalQty.toFixed(2)}</div>
            </div> */}
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Value</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#dc2626' }}>₹{totalValue.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Net Value</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#7c3aed' }}>₹{totalNetValue.toFixed(2)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Purchase Report Generator Component
const PurchaseReportGenerator = () => {
  const today = new Date();
  const [fromDate, setFromDate] = useState(today.toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(today.toISOString().split('T')[0]);
  const [searchBy, setSearchBy] = useState("datewise");
  const [reportType, setReportType] = useState("detail");
  const [filters, setFilters] = useState({
    supplier: ["All Supplier Names"],
    group: ["All Group Names"],
    product: ["All Product Names"]
  });
  const [suppliers, setSuppliers] = useState(["All Supplier Names"]);
  const [groups, setGroups] = useState(["All Group Names"]);
  const [products, setProducts] = useState(["All Product Names"]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [branchProfile, setBranchProfile] = useState({
    branchName: "SUPREME TRADERS",
    address: "",
    phone: "",
    city: ""
  });
  const [purchaseData, setPurchaseData] = useState([]);
  const [productGroupMap, setProductGroupMap] = useState({});
  const [groupsMap, setGroupsMap] = useState({});

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  // Get available report types based on search criteria
  const getAvailableReportTypes = () => {
    switch (searchBy) {
      case "datewise":
      case "supplierwise":
        return [
          { value: "detail", label: "Detail" },
          { value: "abstract", label: "Abstract" }
        ];
      case "groupwise":
      case "productwise":
        return [
          { value: "abstract", label: "Abstract" }
        ];
      case "invoicewise":
        return [
          { value: "detail", label: "Detail" }
        ];
      default:
        return [
          { value: "detail", label: "Detail" },
          { value: "abstract", label: "Abstract" }
        ];
    }
  };

  // Update report type when search criteria changes
  useEffect(() => {
    const availableTypes = getAvailableReportTypes();
    if (!availableTypes.some(type => type.value === reportType)) {
      setReportType(availableTypes[0].value);
    }
  }, [searchBy, reportType]);

  // Firebase Auth and Data Loading
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (user) {
        loadFirebaseData(user);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const loadFirebaseData = async (user) => {
    const encodedEmail = encodeEmailForPath(user.email);

    // Load Branch Profile
    const branchProfileRef = ref(database, `users/${encodedEmail}/branchProfiles`);
    onValue(branchProfileRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const profiles = Object.values(data);
        const latestProfile = profiles[profiles.length - 1];
        setBranchProfile({
          branchName: latestProfile.branchName || "SUPREME TRADERS",
          address: `${latestProfile.address1 || ''} ${latestProfile.address2 || ''}`.trim(),
          phone: latestProfile.mobileNo || '',
          city: latestProfile.city || getCityFromAddress(`${latestProfile.address1 || ''} ${latestProfile.address2 || ''}`)
        });
      }
    });

    // Load Suppliers
    const suppliersRef = ref(database, `users/${encodedEmail}/suppliers`);
    onValue(suppliersRef, (snapshot) => {
      const suppliersData = ["All Supplier Names"];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const supplier = childSnapshot.val();
          if (supplier.supplierName && !suppliersData.includes(supplier.supplierName)) {
            suppliersData.push(supplier.supplierName);
          }
        });
      }
      setSuppliers(suppliersData);
    });

    // Load Groups first and create mapping
    const groupsRef = ref(database, `users/${encodedEmail}/productGroups`);
    onValue(groupsRef, (snapshot) => {
      const groupsData = ["All Group Names"];
      const groupsMapping = {};
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const group = childSnapshot.val();
          if (group.name && !groupsData.includes(group.name)) {
            groupsData.push(group.name);
            // Map both the key and the name to the actual group name
            groupsMapping[childSnapshot.key] = group.name;
            groupsMapping[group.name] = group.name;
          }
        });
      }
      setGroups(groupsData);
      setGroupsMap(groupsMapping);
      
      // After groups are loaded, load products
      loadProductsWithGroups(encodedEmail, groupsMapping);
    });

    return () => {
      off(branchProfileRef);
      off(suppliersRef);
      off(groupsRef);
    };
  };

  const loadProductsWithGroups = (encodedEmail, groupsMapping) => {
    // Load Products and map them to groups
    const productsRef = ref(database, `users/${encodedEmail}/products`);
    onValue(productsRef, (snapshot) => {
      const productsData = ["All Product Names"];
      const groupMap = {};
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const product = childSnapshot.val();
          if (product.productName && !productsData.includes(product.productName)) {
            productsData.push(product.productName);
            
            // Enhanced group mapping logic
            let groupName = 'No Group Assigned';
            
            // Check if product has a direct groupName property
            if (product.groupName && product.groupName !== '') {
              groupName = product.groupName;
            }
            // Check if product has a group property that maps to a group key
            else if (product.group && groupsMapping[product.group]) {
              groupName = groupsMapping[product.group];
            }
            // Check if product.group is already a group name
            else if (product.group && Object.values(groupsMapping).includes(product.group)) {
              groupName = product.group;
            }
            
            groupMap[product.productName] = groupName;
          }
        });
      }
      setProducts(productsData);
      setProductGroupMap(groupMap);
      
      // After products are loaded with proper group mapping, load invoices
      loadInvoicesWithGroupMapping(encodedEmail, groupMap);
    });
  };

  const loadInvoicesWithGroupMapping = (encodedEmail, groupMap) => {
    // Load Purchase Invoice Data
    const invoicesRef = ref(database, `users/${encodedEmail}/invoices`);
    onValue(invoicesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const invoiceRecords = [];
        
        Object.entries(data).forEach(([invoiceId, invoice]) => {
          if (invoice.items && Array.isArray(invoice.items) && invoice.supplierDetails) {
            // Check if IGST is applied for this invoice
            const isIgstApplied = invoice.summary?.igstAmt > 0;
            
            invoice.items.forEach(item => {
              if (item.item && item.qty > 0) {
                const qty = parseFloat(item.qty) || 0;
                const pRate = parseFloat(item.pRate) || 0;
                const discAmt = parseFloat(item.discAmt) || 0;
                const cgstPercent = parseFloat(item.cgstPercent) || 0;
                const sgstPercent = parseFloat(item.sgstPercent) || 0;
                const cessPercent = parseFloat(item.cessPercent) || 0;
                const grossAmt = qty * pRate;
                const taxableValue = grossAmt - discAmt;
                
                let cgstAmt = 0;
                let sgstAmt = 0;
                let igstAmt = 0;
                
                if (isIgstApplied) {
                  // When IGST is applied, combine CGST and SGST rates
                  const combinedRate = cgstPercent + sgstPercent;
                  igstAmt = (taxableValue * combinedRate) / 100;
                } else {
                  // When CGST/SGST is applied separately
                  cgstAmt = (taxableValue * cgstPercent) / 100;
                  sgstAmt = (taxableValue * sgstPercent) / 100;
                }
                
                const cessAmt = (taxableValue * cessPercent) / 100;
                const totalGstAmt = cgstAmt + sgstAmt + igstAmt;
    
                // Get the correct group name from productGroupMap
                const groupName = groupMap[item.item] || 'No Group Assigned';
    
                invoiceRecords.push({
                  invoiceId: invoiceId,
                  invoiceNo: invoice.supplierDetails.invoiceNo || 'N/A',
                  invoiceDate: invoice.supplierDetails.invoiceDate || "",
                  supplier: invoice.supplierDetails.supplier || "Unknown Supplier",
                  productName: item.item || "Unknown Product",
                  groupName: groupName,
                  batch: item.batch || "",
                  eDate: item.eDate || "",
                  mrnNo: invoice.supplierDetails.mrnNo,
                  qty: qty,
                  pRate: pRate,
                  grossAmt: grossAmt,
                  discAmt: discAmt,
                  cgstAmt: cgstAmt,
                  sgstAmt: sgstAmt,
                  igstAmt: igstAmt,
                  cessAmt: cessAmt,
                  totalGstAmt: totalGstAmt,
                  netValue: parseFloat(item.netValue) || 0
                });
              }
            });
          }
        });
        
        setPurchaseData(invoiceRecords);
      } else {
        setPurchaseData([]);
      }
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleGenerateReport = () => {
    setShowReport(true);
  };

const handlePrint = () => {
  const printContent = (
    <PurchaseReportDisplay 
      formData={{ fromDate, toDate, searchBy, reportType }}
      filters={filters}
      branchProfile={branchProfile}
      purchaseData={purchaseData}
    />
  );

  const htmlString = ReactDOMServer.renderToString(printContent);

  const printWindow = window.open('', '', 'width=1200,height=800');
  printWindow.document.write(`
    <html>
      <head>
        <title>Purchase Report</title>
        <style>
          body { 
            margin: 0; 
            padding: 5px; 
            font-family: Arial, sans-serif; 
            font-size: 8px;
          }
          @page { 
            size: A4 landscape; 
            margin: 5mm; 
          }
          table { 
            page-break-inside: auto;
            width: 100%;
            font-size: 7px;
            table-layout: fixed;
            border-collapse: collapse;
          }
          tr { 
            page-break-inside: avoid; 
            page-break-after: auto; 
          }
          th, td {
              padding: 4px !important;
            font-size: 12px !important;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 60px;
          }
          
          /* Column widths for invoicewise detail report */
          .invoicewise-detail th:nth-child(1), .invoicewise-detail td:nth-child(1) { width: 30px; } /* S.No */
          .invoicewise-detail th:nth-child(2), .invoicewise-detail td:nth-child(2) { width: 50px; } /* Invoice No */
          .invoicewise-detail th:nth-child(3), .invoicewise-detail td:nth-child(3) { width: 50px; } /* Invoice Date */
          .invoicewise-detail th:nth-child(4), .invoicewise-detail td:nth-child(4) { width: 80px; } /* Supplier */
          .invoicewise-detail th:nth-child(5), .invoicewise-detail td:nth-child(5) { width: 80px; } /* Product */
          .invoicewise-detail th:nth-child(6), .invoicewise-detail td:nth-child(6) { width: 40px; } /* Qty */
          .invoicewise-detail th:nth-child(7), .invoicewise-detail td:nth-child(7) { width: 40px; } /* PRate */
          .invoicewise-detail th:nth-child(8), .invoicewise-detail td:nth-child(8) { width: 50px; } /* Gross */
          .invoicewise-detail th:nth-child(9), .invoicewise-detail td:nth-child(9) { width: 45px; } /* Disc */
          .invoicewise-detail th:nth-child(10), .invoicewise-detail td:nth-child(10) { width: 50px; } /* CGST */
          .invoicewise-detail th:nth-child(11), .invoicewise-detail td:nth-child(11) { width: 50px; } /* SGST */
          .invoicewise-detail th:nth-child(12), .invoicewise-detail td:nth-child(12) { width: 50px; } /* GST */
          .invoicewise-detail th:nth-child(13), .invoicewise-detail td:nth-child(13) { width: 60px; } /* Net Value */
          
          /* Column widths for abstract reports */
          .abstract th:nth-child(1), .abstract td:nth-child(1) { width: 30px; } /* S.No */
          .abstract th:nth-child(2), .abstract td:nth-child(2) { width: 120px; } /* Key column */
          .abstract th:nth-child(3), .abstract td:nth-child(3) { width: 60px; } /* Value */
          .abstract th:nth-child(4), .abstract td:nth-child(4) { width: 50px; } /* Disc */
          .abstract th:nth-child(5), .abstract td:nth-child(5) { width: 50px; } /* CGST */
          .abstract th:nth-child(6), .abstract td:nth-child(6) { width: 50px; } /* SGST */
          .abstract th:nth-child(7), .abstract td:nth-child(7) { width: 50px; } /* GST */
          .abstract th:nth-child(8), .abstract td:nth-child(8) { width: 70px; } /* Net Value */
          
          /* Column widths for productwise abstract */
          .productwise-abstract th:nth-child(1), .productwise-abstract td:nth-child(1) { width: 30px; } /* S.No */
          .productwise-abstract th:nth-child(2), .productwise-abstract td:nth-child(2) { width: 100px; } /* Product */
          .productwise-abstract th:nth-child(3), .productwise-abstract td:nth-child(3) { width: 60px; } /* Commodity */
          .productwise-abstract th:nth-child(4), .productwise-abstract td:nth-child(4) { width: 50px; } /* Barcode */
          .productwise-abstract th:nth-child(5), .productwise-abstract td:nth-child(5) { width: 40px; } /* Qty */
          .productwise-abstract th:nth-child(6), .productwise-abstract td:nth-child(6) { width: 50px; } /* Value */
          .productwise-abstract th:nth-child(7), .productwise-abstract td:nth-child(7) { width: 45px; } /* Disc */
          .productwise-abstract th:nth-child(8), .productwise-abstract td:nth-child(8) { width: 50px; } /* CGST */
          .productwise-abstract th:nth-child(9), .productwise-abstract td:nth-child(9) { width: 50px; } /* SGST */
          .productwise-abstract th:nth-child(10), .productwise-abstract td:nth-child(10) { width: 50px; } /* GST */
          .productwise-abstract th:nth-child(11), .productwise-abstract td:nth-child(11) { width: 60px; } /* Net Value */
          
          /* Column widths for supplierwise detail */
          .supplierwise-detail th:nth-child(1), .supplierwise-detail td:nth-child(1) { width: 30px; } /* S.No */
          .supplierwise-detail th:nth-child(2), .supplierwise-detail td:nth-child(2) { width: 60px; } /* MRN No */
          .supplierwise-detail th:nth-child(3), .supplierwise-detail td:nth-child(3) { width: 50px; } /* MRN Date */
          .supplierwise-detail th:nth-child(4), .supplierwise-detail td:nth-child(4) { width: 60px; } /* Gross */
          .supplierwise-detail th:nth-child(5), .supplierwise-detail td:nth-child(5) { width: 50px; } /* Disc */
          .supplierwise-detail th:nth-child(6), .supplierwise-detail td:nth-child(6) { width: 50px; } /* CGST */
          .supplierwise-detail th:nth-child(7), .supplierwise-detail td:nth-child(7) { width: 50px; } /* SGST */
          .supplierwise-detail th:nth-child(8), .supplierwise-detail td:nth-child(8) { width: 50px; } /* GST */
          .supplierwise-detail th:nth-child(9), .supplierwise-detail td:nth-child(9) { width: 45px; } /* Others */
          .supplierwise-detail th:nth-child(10), .supplierwise-detail td:nth-child(10) { width: 60px; } /* Net Value */
          
          /* Default detail columns */
          .default-detail th:nth-child(1), .default-detail td:nth-child(1) { width: 30px; } /* S.No */
          .default-detail th:nth-child(2), .default-detail td:nth-child(2) { width: 100px; } /* Product */
          .default-detail th:nth-child(3), .default-detail td:nth-child(3) { width: 40px; } /* Qty */
          .default-detail th:nth-child(4), .default-detail td:nth-child(4) { width: 45px; } /* PRate */
          .default-detail th:nth-child(5), .default-detail td:nth-child(5) { width: 55px; } /* Gross */
          .default-detail th:nth-child(6), .default-detail td:nth-child(6) { width: 45px; } /* Disc */
          .default-detail th:nth-child(7), .default-detail td:nth-child(7) { width: 50px; } /* CGST */
          .default-detail th:nth-child(8), .default-detail td:nth-child(8) { width: 50px; } /* SGST */
          .default-detail th:nth-child(9), .default-detail td:nth-child(9) { width: 50px; } /* GST */
          .default-detail th:nth-child(10), .default-detail td:nth-child(10) { width: 60px; } /* Net Value */
          
          .print-only {
            width: 93% !important;
            max-width: none !important;
            overflow: visible !important;
          }
          div[style*="overflowX"] {
            overflow: visible !important;
          }
        </style>
      </head>
      <body>
        <div id="print-content">${htmlString}</div>
        <script>
          setTimeout(function() {
            window.print();
            window.close();
          }, 1000);
        </script>
      </body>
    </html>
  `);
  printWindow.document.close();
};

  const formData = {
    fromDate,
    toDate,
    searchBy,
    reportType
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        fontSize: '18px',
        color: '#64748b',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <div>Loading Purchase Data...</div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '3rem', 
        color: '#64748b', 
        fontSize: '18px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <h2 style={{ color: '#1e293b', marginBottom: '16px' }}>Authentication Required</h2>
        <p>Please login to access the Purchase Report </p>
      </div>
    );
  }

  const availableReportTypes = getAvailableReportTypes();

  return (
    <div style={{
      width: '100%',
      maxWidth: '90rem',
      backgroundColor: 'white',
      borderRadius: '16px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      padding: '32px',
      margin: '0 auto',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      border: '1px solid #e2e8f0'
    }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @media print {
            body * {
              visibility: hidden;
            }
            .print-only, .print-only * {
              visibility: visible;
            }
            .print-only {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              margin: 0;
              padding: 20px;
              box-shadow: none;
              border: none;
            }
          }
        `}
      </style>
      
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '12px',
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <div style={{
          width: '6px',
          height: '32px',
          backgroundColor: '#3b82f6',
          borderRadius: '3px'
        }}></div>
        <h1 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: '#1e293b',
          margin: '0'
        }}>Purchase Report </h1>
      </div>
      
      {/* Date Range and Search Filters */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '32px',
        padding: '24px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>From Date</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              boxSizing: 'border-box',
              backgroundColor: 'white'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>To Date</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              boxSizing: 'border-box',
              backgroundColor: 'white'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>Search By</label>
          <select
            value={searchBy}
            onChange={(e) => setSearchBy(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              backgroundColor: 'white',
              cursor: 'pointer',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          >
            
            <option value="datewise">Datewise</option>
            <option value="supplierwise">Supplierwise</option>
            <option value="groupwise">Groupwise</option>
            <option value="productwise">Productwise</option>
            <option value="invoicewise">Invoicewise</option>
          </select>
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>Report Type</label>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              backgroundColor: 'white',
              cursor: 'pointer',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          >
            {availableReportTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Filter Checkboxes */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
        marginBottom: '32px',
        
      }}>
        <FilterSection 
          title="Supplier Name" 
          options={suppliers} 
          selected={filters.supplier} 
          onChange={(value) => handleFilterChange("supplier", value)}
        />
        <FilterSection 
          title="Group Name" 
          options={groups} 
          selected={filters.group} 
          onChange={(value) => handleFilterChange("group", value)}
        />
        <FilterSection 
          title="Product Name" 
          options={products} 
          selected={filters.product} 
          onChange={(value) => handleFilterChange("product", value)}
        />
      </div>

      {/* Action Buttons */}
      <div style={{ 
        display: 'flex', 
        gap: '16px', 
        marginBottom: '32px',
        justifyContent: 'center'
      }}>
        <button
          onClick={handleGenerateReport}
          style={{
            background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '140px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Generate Report
        </button>
        <button
          onClick={handlePrint}
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '120px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Print Report
        </button>
      </div>

      {/* Purchase Report Display */}
      {showReport && (
        <PurchaseReportDisplay 
          formData={formData}
          filters={filters}
          branchProfile={branchProfile}
          purchaseData={purchaseData}
        />
      )}
    </div>
  );
};

export default PurchaseReportGenerator;